import { keepPreviousData, useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { Building, Building2, ChevronsUpDown } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useMemo, useState } from 'react'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/elements/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/elements/popover'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
} from '@/components/elements/select'
import { useSidebar } from '@/components/elements/sidebar'
import { getAllEntities } from '@/services/entities.service'
import { getOrganizations } from '@/services/organization.service'
import { updateUser } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'

// Switch to a searchable combobox once the selectable list reaches this size,
// so users with many accounts/orgs can type to filter instead of scrolling.
const SEARCH_THRESHOLD = 10

// Option values are namespaced so an org and a facility can't collide on id and
// so the change handler knows which context to switch into.
const ORG_PREFIX = 'org:'
const ENTITY_PREFIX = 'entity:'
const XVERSE_ADMIN_VALUE = 'xverse-admin'

const EntitySelector = observer(() => {
  const { activeEntity, activeOrganization, organizationRoles, user, setUser } =
    useContext(UserStoreContext)
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const [open, setOpen] = useState(false)

  // Xverse Admins can switch into any facility; everyone else only sees their own.
  const { data: entities } = useQuery({
    queryKey: ['all-entities'],
    queryFn: () => getAllEntities(),
    placeholderData: keepPreviousData,
    enabled: user?.isXverseAdmin,
  })

  // Xverse Admins can also switch into any organization — org membership only
  // covers Owners/Admins, so without this an admin can't reach orgs they
  // provisioned. Members get their orgs from the login payload (no fetch).
  const { data: allOrganizations } = useQuery({
    queryKey: ['organizations', 'all'],
    queryFn: () => getOrganizations({ pagination: { pageSize: 1000, pageIndex: 0 } }),
    placeholderData: keepPreviousData,
    enabled: user?.isXverseAdmin,
  })

  const OPTIONS = useMemo(() => {
    const options = {}

    if (user.isXverseAdmin) {
      options.Admin = [{ value: XVERSE_ADMIN_VALUE, label: 'Xverse Admin' }]
    }

    // Organizations: the user's own memberships (from the login payload) plus,
    // for Xverse Admins, every org (fetched). Deduped by id.
    const orgsById = new Map()
    ;(organizationRoles ?? []).forEach((role) => {
      orgsById.set(role.organizationId, role.organizationName)
    })
    ;(allOrganizations?.results ?? []).forEach((org) => {
      if (!orgsById.has(org.id)) orgsById.set(org.id, org.name)
    })
    if (orgsById.size) {
      options.Organizations = Array.from(orgsById, ([id, name]) => ({
        value: `${ORG_PREFIX}${id}`,
        label: name,
      }))
    }

    if (user.entityRoles?.length) {
      const userEntities = _.uniqBy(
        user.entityRoles.map((role) => role.entity),
        'id',
      )
      options['Your Accounts'] = userEntities.map((entity) => ({
        value: `${ENTITY_PREFIX}${entity.id}`,
        label: entity.name,
      }))
    }

    if (user.isXverseAdmin) {
      const ownIds = new Set((options['Your Accounts'] ?? []).map((o) => o.value))
      options['Other Accounts'] = (entities ?? [])
        .filter((entity) => !ownIds.has(`${ENTITY_PREFIX}${entity.id}`))
        .map((entity) => ({ value: `${ENTITY_PREFIX}${entity.id}`, label: entity.name }))
    }

    return options
  }, [user, entities, organizationRoles, allOrganizations])

  const flatOptions = useMemo(() => Object.values(OPTIONS).flat(), [OPTIONS])
  const useSearch = flatOptions.length >= SEARCH_THRESHOLD

  const VALUE = useMemo(() => {
    if (activeOrganization) return `${ORG_PREFIX}${activeOrganization}`
    if (activeEntity) return `${ENTITY_PREFIX}${activeEntity.id || activeEntity}`
    if (user.isXverseAdmin) return XVERSE_ADMIN_VALUE
    return null
  }, [activeOrganization, activeEntity, user.isXverseAdmin])

  // Resolve the label + logo for the current selection.
  const activeDetails = useMemo(() => {
    if (!VALUE) return null
    if (VALUE === XVERSE_ADMIN_VALUE) return { name: 'Xverse Admin' }

    if (VALUE.startsWith(ORG_PREFIX)) {
      const orgId = VALUE.slice(ORG_PREFIX.length)
      const role = organizationRoles?.find((r) => r.organizationId === orgId)
      const fetched = allOrganizations?.results?.find((o) => o.id === orgId)
      return { name: role?.organizationName ?? fetched?.name ?? 'Organization' }
    }

    const entityId = VALUE.slice(ENTITY_PREFIX.length)
    const fromRoles = user.entityRoles?.find((r) => r.entity?.id === entityId)?.entity
    if (fromRoles) return fromRoles
    return entities?.find((e) => e.id === entityId) || null
  }, [VALUE, user.entityRoles, entities, organizationRoles, allOrganizations])

  // Orgs and facilities get distinct fallback glyphs so the switcher reads at a
  // glance which tier you're in. Orgs never carry a logo here (their details are
  // name-only), so they always fall back to the parent-building icon.
  const isOrgSelection = VALUE?.startsWith(ORG_PREFIX)
  const FallbackIcon = isOrgSelection ? Building2 : Building

  const logoElement = activeDetails?.logo ? (
    <img
      src={activeDetails.logo}
      alt={activeDetails.name}
      className="h-10 max-w-24 rounded object-contain"
    />
  ) : (
    <FallbackIcon className="h-10 w-10 stroke-white" />
  )

  const collapsedLogoElement = activeDetails?.logo ? (
    <img
      src={activeDetails.logo}
      alt={activeDetails.name}
      className="size-5 rounded object-contain"
    />
  ) : (
    <FallbackIcon className="size-5 stroke-white" />
  )

  const handleValueChange = async (value) => {
    if (!value) return
    const updatedUser = { id: user.id }

    if (value === XVERSE_ADMIN_VALUE) {
      updatedUser.activeEntity = null
      updatedUser.activeOrganization = null
    } else if (value.startsWith(ORG_PREFIX)) {
      updatedUser.activeOrganization = value.slice(ORG_PREFIX.length)
      updatedUser.activeEntity = null
    } else {
      updatedUser.activeEntity = value.slice(ENTITY_PREFIX.length)
      updatedUser.activeOrganization = null
    }

    const response = await updateUser(updatedUser)
    setUser(response)
  }

  // --- Trigger (shared between the Select and Combobox variants) -------------
  const triggerInner = isCollapsed ? (
    collapsedLogoElement
  ) : (
    <ChevronsUpDown className="h-6 w-6 text-white" />
  )

  // --- Searchable combobox variant (>= SEARCH_THRESHOLD options) -------------
  const renderCombobox = (trigger) => (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command
          filter={(value, search) => {
            if (!search) return 1
            const label = flatOptions.find((o) => o.value === value)?.label ?? ''
            return label.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }}
        >
          <CommandInput placeholder="Search accounts..." className="h-9" />
          <CommandList>
            <CommandEmpty>No accounts found.</CommandEmpty>
            {Object.entries(OPTIONS).map(([groupLabel, groupOptions]) => (
              <CommandGroup key={groupLabel} heading={groupLabel}>
                {groupOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={(value) => {
                      handleValueChange(value)
                      setOpen(false)
                    }}
                  >
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )

  // --- Native select variant (small lists) -----------------------------------
  const renderSelect = (trigger) => (
    <Select onValueChange={handleValueChange} value={VALUE}>
      {trigger}
      <SelectContent>
        {Object.entries(OPTIONS).map(([groupLabel, groupOptions]) => (
          <SelectGroup key={groupLabel}>
            <SelectLabel>{groupLabel}</SelectLabel>
            {groupOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  )

  // Collapsed sidebar — the logo is the click target.
  if (isCollapsed) {
    const collapsedTrigger = useSearch ? (
      <button
        type="button"
        className="flex h-auto w-auto items-center justify-center border-0 bg-transparent p-0 hover:opacity-80"
        aria-label="Select Account"
      >
        {triggerInner}
      </button>
    ) : (
      <SelectTrigger
        className="h-auto w-auto border-0 bg-transparent p-0 hover:opacity-80"
        showIcon={false}
        aria-label="Select Account"
      >
        {triggerInner}
      </SelectTrigger>
    )

    return (
      <div className="bg-xverse flex h-8 w-full items-center justify-center rounded-md">
        {useSearch ? renderCombobox(collapsedTrigger) : renderSelect(collapsedTrigger)}
      </div>
    )
  }

  // Expanded sidebar — logo + name on the left, dropdown trigger on the right.
  const expandedTrigger = useSearch ? (
    <button
      type="button"
      className="bg-sidebar hover:bg-sidebar-accent w-auto rounded border-0 p-1"
      aria-label="Select Account"
    >
      {triggerInner}
    </button>
  ) : (
    <SelectTrigger
      className="bg-sidebar hover:bg-sidebar-accent w-auto rounded border-0 p-1"
      showIcon={false}
      aria-label="Select Account"
    >
      {triggerInner}
    </SelectTrigger>
  )

  return (
    <div className="bg-xverse flex min-h-14 w-full items-center gap-2 rounded-md px-3 py-2">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {logoElement}
        <span className="truncate font-bold text-white uppercase">
          {activeDetails?.name || 'Select Account'}
        </span>
      </div>

      {useSearch ? renderCombobox(expandedTrigger) : renderSelect(expandedTrigger)}
    </div>
  )
})

export default EntitySelector
